import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Accordion, Badge, Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import type {AfriexVerificationInfo} from '@/types';
import {AfriexVerificationStatus} from '@/types';
import {AfriexKYCProcessor} from '@/types/AfriexKYCProcessor';
import type {
  ResultInfo,
  StripeResultInfo,
} from '@/types/AfriexVerificationInfo';
import * as formatDate from '@/utils/date';

type KYCVerificationViewProps = {
  processor: AfriexKYCProcessor;
  verification: AfriexVerificationInfo;
  handleDeleteSession: () => void;
};
const KYCVerificationView = ({
  processor,
  verification,
  handleDeleteSession,
}: KYCVerificationViewProps) => {
  const getStatusBadgeClasses = (
    status: AfriexVerificationStatus | string | undefined,
  ): string => {
    switch (status) {
      case AfriexVerificationStatus.Success:
        return 'green';
      case AfriexVerificationStatus.Pending:
        return 'pending';
      case AfriexVerificationStatus.Failed:
        return 'red';
      default:
        return 'refunded';
    }
  };
  const ssnLast4 = (verification?.result as StripeResultInfo)?.last4;
  // Core deliberately leaves `AfriexVerificationInfo.result` as `any` (its
  // shape genuinely varies by processor), but for Sumsub it is always this
  // consistent shape, which `AfriexKYCResultInfo` (aliased `ResultInfo`)
  // already models and this file already relies on for `StripeResultInfo`
  // above; reused rather than re-derived.
  const sumsubResult = verification?.result as ResultInfo | undefined;

  return (
    <div className="px-3 pt-3 my-8">
      <h3 className="font-semibold text-gray-900 mb-2 dark:text-gray-200">
        {`${processor} Verification`.toLocaleUpperCase()}
      </h3>
      <hr className="mb-5 dark:border-dark-borderHeavy" />

      <div>
        {/* Status */}
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm flex-[0.4] dark:text-slate-100">
            Status
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            <Badge colorScheme={getStatusBadgeClasses(verification.status)}>
              {verification?.status
                ? verification.status.charAt(0).toUpperCase() +
                  verification.status.slice(1)
                : '-'}
            </Badge>
          </p>
        </div>

        {/* SSN */}
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 text-sm flex-[0.4] dark:text-slate-100">
            SSN Last 4 Digits
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {ssnLast4 || '-'}
          </p>
        </div>

        {/* Attempts */}
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
            Attempts
          </p>
          <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
            {verification?.attempts || '-'}
          </p>
        </div>

        {/* Reject Reason */}
        {processor === AfriexKYCProcessor.SumSub &&
          verification?.status === AfriexVerificationStatus.Failed && (
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
                Rejection Reason
              </p>
              <p className="text-sm flex-[0.6] font-medium text-red-600 w-1/2">
                {sumsubResult?.result?.answer?.reviewResult?.clientComment ??
                  'No specific reason provided'}
              </p>
            </div>
          )}

        {/* Reject Type */}
        {processor === AfriexKYCProcessor.SumSub &&
          verification?.status === AfriexVerificationStatus.Failed && (
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
                Rejection Type
              </p>
              <p className="text-sm font-medium flex-[0.6] text-slate-700 w-1/2 dark:text-slate-100">
                {sumsubResult?.result?.answer?.reviewResult?.reviewRejectType ??
                  'No rejection type provided'}
              </p>
            </div>
          )}

        {/* Reject Labels */}
        {processor === AfriexKYCProcessor.SumSub &&
          verification?.status === AfriexVerificationStatus.Failed && (
            <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
              <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
                Rejection Labels
              </p>
              <p className="text-sm font-medium flex-[0.6] text-red-600 w-1/2 inline-flex space-x-2">
                {sumsubResult?.result?.answer?.reviewResult?.rejectLabels.map(
                  (label: string) => (
                    <span
                      key={label}
                      className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      {label}
                    </span>
                  ),
                )}
              </p>
            </div>
          )}

        {/* Date Created */}
        {processor === AfriexKYCProcessor.SumSub && (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
              Date Created
            </p>
            <p className="text-sm font-medium flex-[0.6] text-gray-700 w-1/2 dark:text-gray-100">
              {formatDate.DDMonYYYY(
                // `DDMonYYYY` takes a required `string`; when this section
                // renders (SumSub only) `answer.createdAt` is required on the
                // real shape, but `sumsubResult` itself is still `| undefined`
                // (unlike the previous `any` chain, which hid that from the
                // compiler without changing what could actually reach here).
                sumsubResult?.result?.answer?.createdAt as string,
                true,
              )}
            </p>
          </div>
        )}

        {/* Risk Labels */}
        {processor === AfriexKYCProcessor.SumSub &&
          sumsubResult?.result?.details?.riskLabels && (
            <Accordion
              title="Risk Labels"
              headerStyle="border dark:border-dark-borderMid rounded-tl rounded-tr mt-4 py-3"
              headerContent="text-md"
            >
              <div className="mb-2 flex flex-col gap-3 justify-between border dark:bg-gray-900 px-2 dark:border-dark-borderMid py-3 px-3 rounded-bl rounded-br border-t-0">
                {Object.entries(sumsubResult.result.details.riskLabels).map(
                  ([key, value]) => {
                    if (
                      key === 'attemptId' ||
                      key === 'createdAt' ||
                      !Array.isArray(value) ||
                      value.length === 0
                    ) {
                      return null;
                    }

                    let labelTitle = key;
                    if (key === 'email') {
                      labelTitle = 'Email labels';
                    } else if (key === 'phone') {
                      labelTitle = 'Phone labels';
                    } else if (key === 'device') {
                      labelTitle = 'Device labels';
                    } else if (key === 'deviceCheck') {
                      labelTitle = 'Device Check';
                    } else if (key === 'crossCheck') {
                      labelTitle = 'Cross Check';
                    } else if (key === 'selfie') {
                      labelTitle = 'Selfie labels';
                    } else if (key === 'aml') {
                      labelTitle = 'AML';
                    } else if (key === 'person') {
                      labelTitle = 'Person';
                    } else if (key === 'company') {
                      labelTitle = 'Company';
                    }

                    return (
                      <div
                        key={key}
                        className="flex flex-col gap-2 pb-2 border-b dark:border-dark-borderMid last:border-b-0"
                      >
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {labelTitle}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {value.map((label: string) => (
                            <span
                              key={label}
                              className="inline-flex px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                            >
                              {label === 'pep'
                                ? 'PEP (Politically Exposed Person)'
                                : label
                                    .replace(/([A-Z])/g, ' $1')
                                    .replace(/^./, (str) => str.toUpperCase())}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </Accordion>
          )}

        {/* Timeline */}
        {(verification?.timeline?.length ?? 0) > 0 && (
          <div className="mb-2 pt-2 flex flex-col gap-3 justify-between">
            <p className="text-gray-700 font-medium dark:text-gray-300">
              Timeline
            </p>
            {verification?.timeline?.map((item) => (
              <div
                key={item.id}
                className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900"
              >
                <p className="text-slate-500 flex-[0.4] text-sm dark:text-slate-100">
                  {item.title} on{' '}
                  <span className="text-gray-700 dark:text-gray-100">
                    {formatDate.DDMonYYYY(item.timestamp, true)}
                  </span>
                </p>
                <p className="text-sm flex-[0.6] font-medium text-slate-500 dark:text-slate-100 w-1/2">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Delete Veriff Session */}
        <PermissionsProvider permission={AfriexAdminPermission.KYCUpdate}>
          {processor === AfriexKYCProcessor.Veriff && (
            <div className="mb-2 pb-2 px-3 flex items-center gap-3 justify-between">
              <p className="text-slate-500 text-sm dark:text-slate-100">
                Handle Veriff Session
              </p>
              <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
                <Button colorScheme="red" onClick={handleDeleteSession}>
                  Delete Veriff Session
                </Button>
              </p>
            </div>
          )}
        </PermissionsProvider>
      </div>
    </div>
  );
};

export default KYCVerificationView;
